import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackFunnel } from '../lib/funnel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 80; // ~4 minutes

const WizardResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const attemptsRef = useRef(0);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');

  const resultId = location.state?.result_id;
  const trustId = location.state?.trust_id;
  const email = location.state?.email;
  const name = location.state?.name || 'there';

  useEffect(() => {
    if (!location.state || !resultId || !email) {
      navigate('/');
      return;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/wizard/status/${resultId}`);
        const s = res.data?.status;
        if (s === 'ready') {
          setStatus('ready');
          trackFunnel('results_generated');
          navigate('/set-password', {
            state: {
              name,
              email,
              result_id: resultId,
              trust_id: trustId
            },
            replace: true
          });
          return;
        }
        if (s === 'failed') {
          setStatus('failed');
          setError(res.data?.error || 'Generation failed. Please try again.');
          return;
        }
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus('failed');
          setError('This is taking longer than expected. Please try again.');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus('failed');
          setError('Could not reach the server. Please try again.');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 sm:px-6 py-12">
      <div className="text-center max-w-md w-full" data-testid="wizard-results-page">
        {status === 'failed' ? (
          <>
            <h1
              className="text-3xl sm:text-4xl font-normal mb-4 leading-tight"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Something went wrong
            </h1>
            <p className="text-base sm:text-lg text-[#1a1a1a]/70 mb-6 sm:mb-8" data-testid="wizard-results-error">
              {error}
            </p>
            <button
              onClick={() => navigate('/wizard')}
              className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors px-6 h-12 rounded-md w-full sm:w-auto"
            >
              Start over
            </button>
          </>
        ) : (
          <>
            <div className="mb-6 sm:mb-8">
              <Loader2 className="w-14 h-14 sm:w-16 sm:h-16 mx-auto text-[#0a5c44] animate-spin" />
            </div>

            <h1
              className="text-3xl sm:text-4xl font-normal mb-4 leading-tight"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Generating your compliance roadmap...
            </h1>

            <p className="text-base sm:text-lg text-[#1a1a1a]/70 mb-6 sm:mb-8 leading-relaxed">
              Thanks, {name}. We're analyzing your responses and building your
              personalized compliance package. This usually takes 30-60 seconds.
            </p>

            <div className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-5 sm:p-6 text-left">
              <p className="text-sm text-[#1a1a1a]/80 mb-3 sm:mb-4">
                <strong>What we're building for you:</strong>
              </p>
              <ul className="space-y-2 text-sm text-[#1a1a1a]/70">
                <li>· A prioritised compliance roadmap</li>
                <li>· Three tailored policy documents</li>
                <li>· Your public trust page</li>
                <li>· A monthly compliance checklist</li>
              </ul>
            </div>

            <p className="text-xs text-[#6b7280] mt-5 sm:mt-6">
              Please keep this tab open. We'll redirect you as soon as it's ready.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default WizardResults;
